import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AccessibilityProvider } from "@/lib/accessibility";
import { SavedHomesProvider } from "@/lib/savedHomes";
import Navbar from "@/layout/Navbar";
import Footer from "@/layout/Footer";
import AccessibilityToolbar from "@/layout/AccessibilityToolbar";
import SavedHomesIndicator from "@/components/SavedHomesIndicator";

import HomePage from "@/pages/HomePage";
import VacanciesPage from "@/pages/VacanciesPage";
import VacancyDetailPage from "@/pages/VacancyDetailPage";
import ReferralsPage from "@/pages/ReferralsPage";
import ParticipantFormPage from "@/pages/ParticipantFormPage";
import CoordinatorFormPage from "@/pages/CoordinatorFormPage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import LandingPage from "@/pages/LandingPage";
import ForParticipantsPage from "@/pages/ForParticipantsPage";
import ForCoordinatorsPage from "@/pages/ForCoordinatorsPage";
import ForFamiliesPage from "@/pages/ForFamiliesPage";
import AboutPage from "@/pages/AboutPage";
import OurValuesPage from "@/pages/OurValuesPage";
import ContactPage from "@/pages/ContactPage";
import CareersPage from "@/pages/CareersPage";
import ResourcesPage, { BlogPostPage } from "@/pages/ResourcesPage";
import FAQPage from "@/pages/FAQPage";
import LocationsPage from "@/pages/LocationsPage";
import EasyReadPage from "@/pages/EasyReadPage";
import SavedHomesPage from "@/pages/SavedHomesPage";
import { PrivacyPage, TermsPage } from "@/pages/LegalPages";
import { PoliciesHubPage, PolicyDetailPage } from "@/pages/PoliciesPages";
import AdminRoutes from "@/pages/AdminPage";
import PortalRoutes from "@/pages/PortalPages";
import NotFoundPage from "@/pages/NotFoundPage";
import PropertyPage from "@/pages/PropertyPage";
import PropertyEnquirePage from "@/pages/PropertyEnquirePage";
import Chatbot from "@/components/Chatbot";
import RevealProvider from "@/components/RevealProvider";

import { LANDING_SLUGS, LANDING_CITIES } from "@/data/landingContent";
import { SiteStructuredData } from "@/lib/seo";

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
}

function SiteChrome({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && (
        <a href="#alpha-main" className="alpha-skip-link" data-testid="skip-to-content">
          Skip to main content
        </a>
      )}
      {!isAdmin && <Navbar />}
      <main id="alpha-main" className={isAdmin ? "" : "min-h-[60vh]"}>{children}</main>
      {!isAdmin && <Footer />}
      {!isAdmin && <AccessibilityToolbar />}
      {!isAdmin && <Chatbot />}
      {!isAdmin && <SavedHomesIndicator />}
      <RevealProvider />
    </>
  );
}

// Build SEO landing routes: /sil, /sil-melbourne, /sil-perth, etc.
function LandingRoutes() {
  const routes = [];
  for (const slug of LANDING_SLUGS) {
    routes.push(
      <Route key={`l-${slug}`} path={`/${slug}`} element={<LandingWrap slug={slug} />} />
    );
    for (const city of Object.values(LANDING_CITIES)) {
      routes.push(
        <Route
          key={`l-${slug}-${city.slug}`}
          path={`/${slug}-${city.slug}`}
          element={<LandingWrap slug={slug} city={city.slug} />}
        />
      );
    }
  }
  return routes;
}

function LandingWrap({ slug, city }) {
  // Re-use LandingPage and mimic url params via injected props
  return <LandingPage.Wrapper slug={slug} city={city} />;
}

// Wrapper API on LandingPage
LandingPage.Wrapper = function Wrapper({ slug, city }) {
  // Re-render LandingPage by overriding useParams. Simpler: navigate via nested route style
  // React Router v7: we'll create a sub-route element that sets the params via URL
  return <LandingPage.__FromProps slug={slug} city={city} />;
};

// A small adapter: import the actual component and accept direct props
// We implement that adapter below via a wrapping default export — see pages/LandingPage.jsx
// For simplicity, call the component with useParams always.
// If that's an issue, we use the <LandingPage /> as-is against the route params.

// ==== Simpler approach: just register direct routes ====

export default function App() {
  return (
    <AccessibilityProvider>
      <SavedHomesProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SiteChrome>
          <Routes>
            <Route path="/" element={<HomePage />} />

            {/* About cluster */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/our-values" element={<OurValuesPage />} />

            {/* Services */}
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:slug" element={<ServiceDetailPage />} />

            {/* SEO landing pages (short URLs + city variants) */}
            {LANDING_SLUGS.map((slug) => (
              <React.Fragment key={slug}>
                <Route path={`/${slug}`} element={<LandingPage />} />
                {Object.values(LANDING_CITIES).map((c) => (
                  <Route key={`${slug}-${c.slug}`} path={`/${slug}-${c.slug}`} element={<LandingPage />} />
                ))}
              </React.Fragment>
            ))}

            {/* Housing */}
            <Route path="/vacancies" element={<VacanciesPage />} />
            <Route path="/our-homes" element={<VacanciesPage />} />
            <Route path="/homes/:slug/enquire" element={<PropertyEnquirePage />} />
            <Route path="/homes/:slug" element={<PropertyPage />} />
            <Route path="/vacancies/coburg-retreat" element={<PropertyPage slug="coburg-retreat" />} />
            <Route path="/vacancies/aintree-house" element={<PropertyPage slug="aintree-house" />} />
            <Route path="/vacancies/:id" element={<VacancyDetailPage />} />
            <Route path="/saved-homes" element={<SavedHomesPage />} />

            {/* Referrals */}
            <Route path="/referrals" element={<ReferralsPage />} />
            <Route path="/refer-a-participant" element={<ReferralsPage />} />
            <Route path="/referrals/participant" element={<ParticipantFormPage />} />
            <Route path="/referrals/coordinator" element={<CoordinatorFormPage />} />

            {/* Audiences */}
            <Route path="/for-participants" element={<ForParticipantsPage />} />
            <Route path="/for-families" element={<ForFamiliesPage />} />
            <Route path="/for-support-coordinators" element={<ForCoordinatorsPage />} />

            {/* Resources */}
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/:slug" element={<BlogPostPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/locations" element={<LocationsPage />} />
            <Route path="/easy-read" element={<EasyReadPage />} />

            {/* Contact & careers */}
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/careers" element={<CareersPage />} />

            {/* Policies */}
            <Route path="/policies" element={<PoliciesHubPage />} />
            <Route path="/policies/:slug" element={<PolicyDetailPage />} />

            {/* Legal */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />

            {/* Admin */}
            <Route path="/admin/*" element={<AdminRoutes />} />

            {/* Participant portal */}
            <Route path="/portal/*" element={<PortalRoutes />} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </SiteChrome>
        <SiteStructuredData />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
      </SavedHomesProvider>
    </AccessibilityProvider>
  );
}
